import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type { TableActionItem } from '@/components/shared';
import {
  adjustBackofficeOrganizationBalance,
  fetchBackofficeOrganizationTransactions,
} from '../api';
import type {
  AdjustBackofficeOrganizationBalanceInput,
  BackofficeOrganizationTransactionRow,
  OrganizationTransactionTypeFilter,
} from '../types';
import { AdjustOrganizationBalanceModal } from './AdjustOrganizationBalanceModal';
import { OrganizationTransactionsFilters } from './OrganizationTransactionsFilters';
import { OrganizationTransactionsTable } from './OrganizationTransactionsTable';

const DEFAULT_LIMIT = 20;
const TRANSACTION_TYPE_VALUES = new Set<OrganizationTransactionTypeFilter>([
  'all',
  'purchase',
  'voucher',
  'usage',
  'onboarding',
  'adjustment',
]);

export interface OrganizationTransactionsListSectionProps {
  organizationId: string;
  organizationName: string;
  onBalanceAdjusted: () => Promise<void>;
  surface?: 'page' | 'tile' | undefined;
}

function readTransactionType(value: string | null): OrganizationTransactionTypeFilter {
  return value && TRANSACTION_TYPE_VALUES.has(value as OrganizationTransactionTypeFilter)
    ? (value as OrganizationTransactionTypeFilter)
    : 'all';
}

export function OrganizationTransactionsListSection({
  organizationId,
  organizationName,
  onBalanceAdjusted,
  surface = 'page',
}: OrganizationTransactionsListSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState<BackofficeOrganizationTransactionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [adjustingBalance, setAdjustingBalance] = useState(false);
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const loadingMoreRef = useRef(false);
  const refreshAbortControllerRef = useRef<AbortController | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const transactionType = readTransactionType(searchParams.get('transactionType'));
  const requestParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('transactionType', transactionType);
    return params;
  }, [transactionType]);
  const hasMore = transactions.length < total;

  const loadTransactions = useCallback(
    async (offset: number, signal: AbortSignal) => {
      const params = new URLSearchParams(requestParams);
      params.set('limit', String(DEFAULT_LIMIT));
      params.set('offset', String(offset));

      const response = await fetchBackofficeOrganizationTransactions(
        organizationId,
        params,
        signal
      );
      setTotal(response.page.total);
      setTransactions((current) =>
        offset === 0 ? response.transactions : [...current, ...response.transactions]
      );
    },
    [organizationId, requestParams]
  );

  const handleRefresh = useCallback(async () => {
    refreshAbortControllerRef.current?.abort();
    const controller = new AbortController();
    refreshAbortControllerRef.current = controller;

    setRefreshing(true);
    setError(null);
    setLoadMoreError(null);

    try {
      await loadTransactions(0, controller.signal);
    } catch (caught: unknown) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        return;
      }
      setTransactions([]);
      setTotal(0);
      setError(caught instanceof Error ? caught.message : 'Failed to refresh transactions');
    } finally {
      if (refreshAbortControllerRef.current === controller) {
        refreshAbortControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setRefreshing(false);
      }
    }
  }, [loadTransactions]);

  useEffect(() => {
    const controller = new AbortController();
    setInitialLoading(true);
    setError(null);
    setLoadMoreError(null);

    loadTransactions(0, controller.signal)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setTransactions([]);
        setTotal(0);
        setError(caught instanceof Error ? caught.message : 'Failed to load transactions');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setInitialLoading(false);
        }
      });

    return () => controller.abort();
  }, [loadTransactions]);

  useEffect(() => {
    return () => refreshAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || initialLoading || !hasMore) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const controller = new AbortController();
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || loadingMoreRef.current) {
          return;
        }

        observer.disconnect();
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setLoadMoreError(null);
        loadTransactions(transactions.length, controller.signal)
          .catch((caught: unknown) => {
            if (caught instanceof DOMException && caught.name === 'AbortError') {
              return;
            }
            setLoadMoreError(
              caught instanceof Error ? caught.message : 'Failed to load more transactions'
            );
          })
          .finally(() => {
            loadingMoreRef.current = false;
            if (!controller.signal.aborted) {
              setLoadingMore(false);
            }
          });
      },
      { rootMargin: '280px' }
    );

    observer.observe(target);

    return () => {
      controller.abort();
      observer.disconnect();
    };
  }, [hasMore, initialLoading, loadTransactions, transactions.length]);

  const tableActions = useMemo<TableActionItem[]>(
    () => [
      {
        id: 'adjust-balance',
        label: 'Adjust balance',
        onSelect: () => {
          setAdjustmentError(null);
          setAdjustModalOpen(true);
        },
        placement: 'inline-priority',
        variant: 'primary',
        icon: Plus,
      },
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        onSelect: () => {
          void handleRefresh();
        },
        placement: 'fixed-inline',
        disabled: initialLoading,
        isLoading: refreshing,
      },
    ],
    [handleRefresh, initialLoading, refreshing]
  );

  async function handleAdjustmentSubmit(input: AdjustBackofficeOrganizationBalanceInput) {
    setAdjustingBalance(true);
    setAdjustmentError(null);

    try {
      await adjustBackofficeOrganizationBalance(organizationId, input);
      await onBalanceAdjusted();
      await handleRefresh();
      setAdjustModalOpen(false);
    } catch (caught: unknown) {
      setAdjustmentError(caught instanceof Error ? caught.message : 'Failed to adjust balance');
    } finally {
      setAdjustingBalance(false);
    }
  }

  function updateTransactionType(nextType: OrganizationTransactionTypeFilter) {
    const next = new URLSearchParams(searchParams);
    if (nextType === 'all') {
      next.delete('transactionType');
    } else {
      next.set('transactionType', nextType);
    }
    next.delete('offset');
    setSearchParams(next, { replace: true });
  }

  if (initialLoading) {
    return (
      <section
        className={
          surface === 'tile'
            ? 'h-full bg-white'
            : 'rounded-lg border border-neutral-200 bg-white shadow-sm'
        }
      >
        <div className="px-5 py-16 text-center text-body text-neutral-500">
          Loading transactions
        </div>
      </section>
    );
  }

  return (
    <>
      <section
        className={
          surface === 'tile'
            ? 'flex h-full min-h-full flex-col overflow-hidden bg-white'
            : 'rounded-lg border border-neutral-200 bg-white shadow-sm'
        }
      >
        <OrganizationTransactionsFilters
          transactionType={transactionType}
          actions={tableActions}
          onTransactionTypeChange={updateTransactionType}
        />

        {error ? (
          <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
        ) : transactions.length === 0 ? (
          <div className="px-5 py-16 text-center text-body text-neutral-500">
            No transactions found for this organization.
          </div>
        ) : (
          <div className={surface === 'tile' ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
            <OrganizationTransactionsTable
              organizationName={organizationName}
              transactions={transactions}
            />
            <div ref={loadMoreRef} className="px-5 py-4 text-small text-neutral-500">
              {loadingMore
                ? 'Loading more transactions'
                : hasMore
                  ? 'Scroll to load more transactions'
                  : `Showing all ${total} transactions`}
            </div>
            {loadMoreError ? (
              <div className="px-5 pb-4 text-small text-red-700">{loadMoreError}</div>
            ) : null}
          </div>
        )}
      </section>

      {adjustModalOpen ? (
        <AdjustOrganizationBalanceModal
          organizationName={organizationName}
          submitting={adjustingBalance}
          error={adjustmentError}
          onClose={() => {
            if (!adjustingBalance) {
              setAdjustModalOpen(false);
            }
          }}
          onSubmit={handleAdjustmentSubmit}
        />
      ) : null}
    </>
  );
}
